import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import api from '@/lib/api';
import { ArrowLeft, Shield, FileText, Loader2 } from 'lucide-react';

export default function LegalPage() {
  const { slug } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true); setError('');
    api.get(`/legal/${slug}`)
      .then(r => setDoc(r.data))
      .catch(e => setError(e?.response?.data?.detail || 'Page not found.'))
      .finally(() => setLoading(false));
  }, [slug]);

  const Icon = slug === 'privacy' ? Shield : FileText;
  const eyebrow = slug === 'privacy' ? 'Privacy Policy' : 'Terms of Service';

  return (
    <div className="bg-white min-h-screen" data-testid={`legal-page-${slug}`}>
      <section className="relative bg-ink text-white py-12 md:py-16 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none kc-noise opacity-50"/>
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(600px 350px at 80% 50%, rgba(212,164,55,0.15), transparent 60%)' }}/>
        <div className="relative kc-container">
          <Link to="/" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-white/60 hover:text-[color:var(--kc-gold)]" data-testid="legal-back"><ArrowLeft size={12}/> Home</Link>
          <div className="mt-4 flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[color:var(--kc-gold)]/20 border border-[color:var(--kc-gold)]/40 flex items-center justify-center flex-shrink-0">
              <Icon size={20} className="text-[color:var(--kc-gold)]"/>
            </div>
            <div className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] font-semibold">{eyebrow}</div>
          </div>
          <h1 className="mt-3 font-display uppercase leading-[0.95]" style={{ fontSize: 'clamp(2.25rem, 5.5vw, 4rem)' }}>
            {doc?.title || eyebrow}
          </h1>
          {doc?.updated_at && (
            <p className="mt-3 text-xs text-white/55 uppercase tracking-wider" data-testid="legal-updated">
              Last updated · {new Date(doc.updated_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          )}
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="kc-container max-w-3xl">
          {loading && (
            <div className="py-16 text-center text-muted-kc" data-testid="legal-loading">
              <Loader2 className="inline animate-spin mr-2" size={16}/> Loading…
            </div>
          )}
          {!loading && error && (
            <div className="py-12 text-center" data-testid="legal-error">
              <p className="text-[color:var(--kc-danger)]">{error}</p>
              <Link to="/" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-ink hover:text-[color:var(--kc-gold-2)]">Go home</Link>
            </div>
          )}
          {!loading && !error && doc && (
            <article className="legal-prose" data-testid="legal-body">
              <ReactMarkdown>{doc.body || ''}</ReactMarkdown>
            </article>
          )}
          <div className="mt-12 p-5 rounded-[var(--kc-radius-md)] bg-surface-2 border border-kc text-sm text-ink-2">
            Questions? Reach out at <a href="mailto:mickey@kickasslifecoach.com" className="font-semibold text-ink hover:text-[color:var(--kc-gold-2)] underline-offset-2 hover:underline">mickey@kickasslifecoach.com</a>.
          </div>
        </div>
      </section>

      <style>{`
        .legal-prose h1 { font-family: var(--kc-font-display); text-transform: uppercase; font-size: clamp(1.6rem, 3vw, 2.2rem); line-height: 1.05; margin: 0 0 1rem 0; letter-spacing: -0.01em; }
        .legal-prose h2 { font-family: var(--kc-font-display); text-transform: uppercase; font-size: clamp(1.25rem, 2vw, 1.6rem); line-height: 1.15; margin: 2.5rem 0 0.6rem 0; color: var(--kc-ink); letter-spacing: -0.005em; }
        .legal-prose h3 { font-size: 1.05rem; font-weight: 700; margin: 1.6rem 0 0.5rem 0; color: var(--kc-ink); }
        .legal-prose p { font-size: 1rem; line-height: 1.75; color: var(--kc-ink-2); margin: 0 0 1rem 0; }
        .legal-prose ul, .legal-prose ol { margin: 0 0 1.25rem 0; padding-left: 1.5rem; color: var(--kc-ink-2); }
        .legal-prose li { font-size: 1rem; line-height: 1.7; margin: 0.35rem 0; }
        .legal-prose ul li::marker { color: var(--kc-gold-2); }
        .legal-prose strong { color: var(--kc-ink); font-weight: 700; }
        .legal-prose a { color: var(--kc-ink); text-decoration: underline; text-underline-offset: 3px; }
        .legal-prose a:hover { color: var(--kc-gold-2); }
        .legal-prose hr { border: 0; border-top: 1px solid var(--kc-border); margin: 2rem 0; }
        .legal-prose blockquote { border-left: 4px solid var(--kc-gold); padding-left: 1rem; margin: 1.25rem 0; color: var(--kc-ink); font-style: italic; }
      `}</style>
    </div>
  );
}
